import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

export interface FormSectionProps {
  title: string;
  description?: string;
  children: ReactNode;
  /** Field grid columns on wider screens. */
  columns?: 1 | 2;
  /** A running total or status, right-aligned in the panel header. */
  aside?: ReactNode;
  className?: string;
}

/**
 * A titled group of form fields, drawn as its own panel.
 *
 * Every long form in the product — the institute wizard, settings, student
 * admission — is built from these, so field spacing and heading weight never
 * drift between screens. The tinted header and accent rule give a long form a
 * rhythm to scan, instead of one unbroken wall of inputs.
 */
export function FormSection({
  title,
  description,
  children,
  columns = 2,
  aside,
  className,
}: FormSectionProps) {
  return (
    <section className={cn('card', className)}>
      <header className="flex items-start gap-2.5 sm:gap-3 rounded-t-card border-b border-line bg-surface-2 px-4 py-3 sm:px-5">
        <span className="mt-1 h-4 w-1 shrink-0 rounded-full bg-indigo-600 dark:bg-indigo-400" aria-hidden />
        <div className="min-w-0 flex-1">
          <h2 className="text-base">{title}</h2>
          {description && <p className="mt-0.5 text-xs sm:text-sm text-ink-2">{description}</p>}
        </div>
        {aside && <div className="shrink-0">{aside}</div>}
      </header>

      <div
        className={cn(
          'grid grid-cols-1 gap-4 px-4 py-5 sm:px-5',
          columns === 2 && 'sm:grid-cols-2',
        )}
      >
        {children}
      </div>
    </section>
  );
}

/** Makes a field span both columns inside a two-column `FormSection`. */
export function FormFieldWide({ children }: { children: ReactNode }) {
  return <div className="sm:col-span-2">{children}</div>;
}
